(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.TPImageCompatibility = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const RULES = Object.freeze({
    outputDpi: 300,
    maxFileBytes: 20 * 1024 * 1024,
    maxSourcePixels: 100000000,
    acceptedMimeTypes: Object.freeze(['image/jpeg', 'image/png', 'image/webp']),
    thresholds: Object.freeze({ great: 250, good: 180, medium: 120 })
  });

  function finitePositive(value) {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : 0;
  }

  function cmToPx(cm, dpi = RULES.outputDpi) {
    return Math.round((finitePositive(cm) / 2.54) * finitePositive(dpi));
  }

  function classifyDpi(dpi) {
    const rounded = Math.max(0, Math.round(Number(dpi) || 0));
    if (rounded >= RULES.thresholds.great) return { dpi: rounded, code: 'great', label: 'Ottima', requiresConfirmation: false };
    if (rounded >= RULES.thresholds.good) return { dpi: rounded, code: 'good', label: 'Buona', requiresConfirmation: false };
    if (rounded >= RULES.thresholds.medium) return { dpi: rounded, code: 'medium', label: 'Sufficiente', requiresConfirmation: false };
    return { dpi: rounded, code: 'low', label: 'Bassa', requiresConfirmation: true };
  }

  function validateSource(meta) {
    const widthPx = Math.round(finitePositive(meta && meta.widthPx));
    const heightPx = Math.round(finitePositive(meta && meta.heightPx));
    const bytes = Math.round(finitePositive(meta && meta.bytes));
    const mimeType = String((meta && meta.mimeType) || '').toLowerCase();
    const errors = [];

    if (!RULES.acceptedMimeTypes.includes(mimeType)) errors.push({ code: 'mime', message: 'Sono accettate immagini JPG, PNG o WEBP.' });
    if (!widthPx || !heightPx) errors.push({ code: 'dimensions', message: 'Non è stato possibile leggere le dimensioni della fotografia.' });
    if (bytes > RULES.maxFileBytes) errors.push({ code: 'bytes', message: 'Il file supera il limite di 20 MB.' });
    if (widthPx * heightPx > RULES.maxSourcePixels) errors.push({ code: 'pixels', message: 'La fotografia supera il limite di 100 megapixel.' });

    return { ok: errors.length === 0, widthPx, heightPx, bytes, mimeType, errors };
  }

  function placedDpi(input) {
    const naturalWidthPx = finitePositive(input && input.naturalWidthPx);
    const naturalHeightPx = finitePositive(input && input.naturalHeightPx);
    const printWidthCm = finitePositive(input && input.printWidthCm);
    const printHeightCm = finitePositive(input && input.printHeightCm);
    const placedWidthRatio = finitePositive(input && input.placedWidthRatio);
    const placedHeightRatio = finitePositive(input && input.placedHeightRatio);
    const physicalWidthCm = printWidthCm * placedWidthRatio;
    const physicalHeightCm = printHeightCm * placedHeightRatio;
    if (!naturalWidthPx || !naturalHeightPx || !physicalWidthCm || !physicalHeightCm) return 0;
    return Math.round(Math.min(
      naturalWidthPx / (physicalWidthCm / 2.54),
      naturalHeightPx / (physicalHeightCm / 2.54)
    ));
  }

  function coverCrop(input) {
    const sourceWidthPx = finitePositive(input && input.sourceWidthPx);
    const sourceHeightPx = finitePositive(input && input.sourceHeightPx);
    const printWidthCm = finitePositive(input && input.printWidthCm);
    const printHeightCm = finitePositive(input && input.printHeightCm);
    if (!sourceWidthPx || !sourceHeightPx || !printWidthCm || !printHeightCm) {
      return { ok: false, code: 'invalid_dimensions' };
    }

    const sourceRatio = sourceWidthPx / sourceHeightPx;
    const targetRatio = printWidthCm / printHeightCm;
    let usedWidthPx = sourceWidthPx;
    let usedHeightPx = sourceHeightPx;
    if (sourceRatio > targetRatio) usedWidthPx = sourceHeightPx * targetRatio;
    else if (sourceRatio < targetRatio) usedHeightPx = sourceWidthPx / targetRatio;

    const dpi = Math.round(Math.min(
      usedWidthPx / (printWidthCm / 2.54),
      usedHeightPx / (printHeightCm / 2.54)
    ));
    return {
      ok: true,
      mode: 'cover',
      usedWidthPx: Math.round(usedWidthPx),
      usedHeightPx: Math.round(usedHeightPx),
      croppedWidthPx: Math.max(0, Math.round(sourceWidthPx - usedWidthPx)),
      croppedHeightPx: Math.max(0, Math.round(sourceHeightPx - usedHeightPx)),
      result: classifyDpi(dpi)
    };
  }

  function evaluateFileForFormat(meta, format) {
    const source = validateSource(meta);
    if (!source.ok) return { ok: false, source, compatibility: null };
    const compatibility = coverCrop({
      sourceWidthPx: source.widthPx,
      sourceHeightPx: source.heightPx,
      printWidthCm: format && format.widthCm,
      printHeightCm: format && format.heightCm
    });
    return { ok: compatibility.ok, source, compatibility };
  }

  return Object.freeze({ RULES, cmToPx, classifyDpi, validateSource, placedDpi, coverCrop, evaluateFileForFormat });
});
